import { SelectState } from './SelectState';
import {
  action,
  computed,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from 'mobx';
import { CollectionState } from './CollectionState';
import { Snapshot } from './snapshot';
import { addEventListener, TypedEmitter } from '../util';
import { EventEmitter } from 'events';
import { KeyedItem } from '../model';

export interface BulkSelectKeyedItem<T> {
  item: T;
  key: string;
}

export interface BulkSelectStateParams<T> {
  collection: CollectionState<T, any>;
  disableAutoSelectAllCount?: boolean;
  maxSelection?: number;
  scrollToTop: () => unknown;
  useNewSelectAllLogic?: boolean;
}

export class BulkSelectState<T> {
  readonly select: SelectState<BulkSelectKeyedItem<T>>;
  readonly events = new EventEmitter() as TypedEmitter<{
    openModal: () => unknown;
    closeModal: () => unknown;
  }>;
  readonly collection;
  readonly scrollToTop;
  useNewSelectAllLogic;
  disableAutoSelectAllCount?: boolean;
  totalSnapshot;

  get allSelected() {
    return this.select.defaultStatus.isChecked;
  }

  get hasUnchecked() {
    return !!this.uncheckedValues.length;
  }

  constructor(params: Readonly<BulkSelectStateParams<T>>) {
    this.collection = params.collection;
    this.scrollToTop = params.scrollToTop;
    this.disableAutoSelectAllCount = params.disableAutoSelectAllCount;
    this.useNewSelectAllLogic = params.useNewSelectAllLogic;

    this.select = new SelectState<BulkSelectKeyedItem<T>>({
      keyGetter: (item) => item.key,
    });

    this.totalSnapshot = new Snapshot({
      expression: () => this.allSelected,
      effect: () => this.collection.result.total,
    });

    makeObservable(this, {
      allSelected: computed,
      disableAutoSelectAllCount: observable.ref,
      selectAll: action,
      selectOne: action,
      selectOneItem: action,
      deselectOne: action,
      deselectOneItem: action,
      resetToDefaults: action.bound,
      hasUnchecked: computed,
      selectedIds: computed,
      uncheckedIds: computed,
      selectedValues: computed,
      selectedKeyedItems: computed,
      uncheckedValues: computed,
      uncheckedInCurrentCollection: computed,
      selectedCountOrTotal: computed,
      checkedSet: computed,
      indeterminateSet: computed,
    });
  }

  getCollectionItem(key: string) {
    return this.collection.result._keyedItemsMap.get(key);
  }

  get selectedIds() {
    return this.select.selectedKeys;
  }

  get uncheckedIds() {
    return this.select.toArray
      .filter(({ status }) => !status.isChecked)
      .map(({ value }) => value.key);
  }

  get selectedCount(): number {
    return this.select.selectedCount;
  }

  get allSelectedItemsFetched() {
    return this.selectedCountOrTotal <= this.collection.result.items.length;
  }

  get selectedValues() {
    return this.select.selectedValues.map(({ item }) => item);
  }

  get checkedSet() {
    return new Set(
      this.select.toArray
        .filter(({ status }) => status.isChecked)
        .map(({ value }) => value.key),
    );
  }

  get indeterminateSet() {
    return new Set(
      this.select.toArray
        .filter(({ status }) => status.isIndeterminate)
        .map(({ value }) => value.key),
    );
  }

  get uncheckedValues() {
    return this.select.toArray
      .filter(({ status }) => !status.isChecked)
      .map(({ value }) => value.item);
  }

  isChecked(key: string) {
    const { select } = this;
    return select.isChecked(key);
  }

  selectAll() {
    const { select, collection } = this;
    select.defaultStatus = select.defaultStatus.final();
    const {
      result: { keyedItems },
    } = collection;
    select.finalizeMany(keyedItems);
  }

  deselectAll() {
    const { select } = this;
    runInAction(() => {
      select.defaultStatus = select.defaultStatus.start();
    });
    select.startAll();
  }

  selectOneItem(item: BulkSelectKeyedItem<T>) {
    const { select, useNewSelectAllLogic } = this;

    select.set(item);

    if (!useNewSelectAllLogic) {
      runInAction(() => {
        select.defaultStatus = select.defaultStatus.start();
      });
    }
  }

  selectOne(key: string) {
    const { collection } = this;
    const { result } = collection;
    const keyedItem = result.get(key);
    if (keyedItem) {
      this.selectOneItem(keyedItem);
    }
  }

  deselectOneItem(keyedItem: BulkSelectKeyedItem<T>) {
    const { select, useNewSelectAllLogic } = this;

    select.delete(keyedItem);

    if (!useNewSelectAllLogic) {
      runInAction(() => {
        select.defaultStatus = select.defaultStatus.start();
      });
    }
  }

  deselectOne(key: string) {
    const { collection } = this;
    const { result } = collection;
    const keyedItem = result.get(key);

    if (keyedItem == null) {
      return;
    }

    this.deselectOneItem(keyedItem);
  }

  openModal = () => {
    this.events.emit('openModal');
  };

  closeModal = () => {
    this.events.emit('closeModal');
  };

  resetToDefaults() {
    const { select } = this;
    runInAction(() => {
      select.defaultStatus = select.defaultStatus.start();
    });
    select.clear();
  }

  init() {
    const disposers = [
      this.totalSnapshot.init(),
      addEventListener(this.collection.emitter, 'ready', () => {
        this.totalSnapshot.forceUpdate();
      }),
      reaction(
        () => this.collection.result.keyedItems,
        (keyedItems) => {
          const {
            select,
            allSelected,
            disableAutoSelectAllCount,
            collection: {
              result: { _keyedItemsMap },
            },
          } = this;
          if (allSelected && !disableAutoSelectAllCount) {
            select.syncValues(keyedItems);
          } else {
            select.syncValues(
              select.selectedKeys
                .map(
                  (key) =>
                    _keyedItemsMap.get(key) as
                      | BulkSelectKeyedItem<T>
                      | null
                      | undefined,
                )
                .filter((e): e is BulkSelectKeyedItem<T> => e != null),
            );
          }
        },
      ),
    ];

    return () => {
      disposers.forEach((disposer) => disposer());
    };
  }

  get selectedKeyedItems() {
    return this.selectedIds
      .map((id) => this.collection.result.get(id))
      .filter((item): item is KeyedItem<T> => !!item);
  }

  get isEmpty() {
    return this.select.isEmpty;
  }

  get uncheckedInCurrentCollection() {
    const { uncheckedValues, collection } = this;
    const { itemKey } = collection;

    return uncheckedValues.filter((item) =>
      collection.result.has(itemKey(item)),
    );
  }

  get selectedCountOrTotal() {
    const {
      allSelected,
      disableAutoSelectAllCount,
      selectedIds,
      useNewSelectAllLogic,
      uncheckedInCurrentCollection,
      collection: {
        result: { total },
      },
    } = this;

    if (!allSelected || disableAutoSelectAllCount) {
      return selectedIds.length;
    }

    if (useNewSelectAllLogic) {
      return total - uncheckedInCurrentCollection.length;
    }

    return total;
  }

  toggleIsSelectAll = action(() => {
    const {
      allSelected,
      select: { isEmptyEnabledItems },
    } = this;
    if (allSelected || !isEmptyEnabledItems) {
      this.deselectAll();
    } else {
      this.selectAll();
    }
  });

  toggle(keyedItem: BulkSelectKeyedItem<T>) {
    this.select.toggle(keyedItem);
  }
}
